import React, { useEffect, useRef, useState } from "react";
import "./TasksTableField.scss";

import classNames from "classnames";

import { useDispatch } from "react-redux";
import { showToast } from "src/app/methods/showToast";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { AccessLinkMetaField } from "src/app/metafields/AccessLinkMetaField/AccessLinkMetaField";
import CustomCheckbox from "src/app/components/CustomCheckbox/CustomCheckbox";
import { TooltipedEllipsis } from "src/app/components/TooltipedEllipsis/TooltipedEllipsis";
import { Coupon } from "src/redux/coupon/couponReducer";
import { getCouponsList } from "src/redux/coupon/couponActions";
import { MetaFieldType } from "src/app/methods/getMetaFieldTypeOptions";
import { DictionaryElementSelector } from "src/app/dropdowns/DictionaryElementSelector/DictionaryElementSelector";
import AggregatedSelect from "src/app/components/AggregatedSelect/AggregatedSelect";
import { getDefaultKeyBinding } from "draft-js";
import TableFileField from "src/app/metafields/FileField/TableFileField";
import { ReactComponent as PlusIcon } from "../../../images/plus-transparent.svg";
import { ReactComponent as RemoveIcon } from "../../../images/close-alt.svg";
import { ReactComponent as EditIcon } from "../../../images/edit.svg";

import CustomDatePicker, {
  DatePickerWrapper,
} from "../../components/CustomDatePicker/CustomDatePicker";
import { IMember } from "../ProjectTypes";
import ChoosePersonDropdown from "../../dropdowns/ChoosePersonDropdown/ChoosePersonDropdown";

import AvatarPlaceholder from "../../../images/avatar-purple.svg";
import {
  changeTaskMetaFieldValue,
  updateProjectMetaValue,
  setLocalSyncToggle,
  setTableInputFocused,
  changeWorkspaceTaskMetaFieldValue,
  setCurrentTaskId,
  setMessageHistoryPreviewOpen,
} from "../../../redux";
import Select from "../../components/Select/Select";
import useOnClickOutside from "../../methods/useOnClickOutside";
import { FieldSynchronizationWarningModal } from "../../modals/FieldSynchronizationWarningModal/FieldSynchronizationWarningModal";
import { tableDataType } from "../../components/Table/Table";
import TaskTableField from "./components/TaskTableField/TaskTableField";
import { ProjectTableField } from "./components/ProjectTableField/ProjectTableField";
import { ProjectFieldPreviewSwitch } from "./components/ProjectFieldPreviewSwitch";
import { ProjectFieldEditSwitch } from "./components/ProjectFieldEditSwitch";
import { ProgressField } from "./meta-types/ProgressField/ProgressField";
import { Metadata } from "../../Task/Subtask/Subtask";
import { TextBoxWrapper } from "../../components/TextBoxWrapper/TextBoxWrapper";
import SelectDropdown from "../../dropdowns/SelectDropdown/SelectDropdown";
import { TableContext } from "./types";
import ProjectSelector from "../../components/ProjectSelector/ProjectSelector";
import OutreachCount from "./meta-types/OutreachCount/OutreachCount";
import { ContentMonitorScore } from "./meta-types/ContentMonitorScore/ContentMonitorScore";
import { TooltipField } from "./meta-types/TooltipField/TooltipField";
import { ChangeWillAffectManyRowsModal } from "src/app/modals/ChangeWillAffectManyRowsModal/ChangeWillAffectManyRowsModal";
import generateUuid from "src/app/libs/generateUuid";
import { REGEX } from "src/utils/REGEX";
import { useCellCurrencyState } from "src/hooks/useCellCurrencyState";

interface TasksTableFieldInterface {
  activeWorkspaceUuid: string;
  columnKey: string;
  columnName: string;
  couponsList: Coupon[];
  dataType: tableDataType;
  fieldData: Metadata;
  fieldValue: string;
  membersList: IMember[];
  objectId: string;
  rowId: string;
  selectedTasks: any;
  title: string;
  overrideValue?: boolean;
  tableContext: TableContext | undefined;
  projectId: string;
  isReadOnly: boolean;
}

export const ACTION_TYPES = [MetaFieldType.Creator, MetaFieldType.Content];

export default function TasksTableField({
  activeWorkspaceUuid,
  columnKey,
  columnName,
  couponsList,
  dataType,
  fieldData,
  fieldValue,
  membersList,
  objectId,
  rowId,
  selectedTasks,
  title,
  overrideValue,
  tableContext,
  projectId,
  isReadOnly,
}: TasksTableFieldInterface) {
  const {
    data,
    manuallyEdited,
    name,
    shouldTriggerEditWarning,
    type,
    value: fieldVal,
    fieldValueSource,
  } = fieldData;

  const uuid =
    tableContext === "workspaceTasksList"
      ? fieldData.wsGlobalTaskMetaFieldUuid!
      : fieldData.uuid;

  const [key, setKey] = useState(uuid);
  const {
    value,
    setValue,
    cellCurrencyCode,
    hasCustomCurrency,
    handleCurrencyPickerSave,
  } = useCellCurrencyState(fieldValue, data);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [member, setMember] = useState<any>({});
  const [showEditMark, setShowEditMark] = useState(manuallyEdited);
  const [disableFieldClick, setDisableFieldClick] = useState(
    shouldTriggerEditWarning,
  );
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [multiValueToUpdate, setMultiValueToUpdate] = useState<any>(null);
  const [multiCurrencyCodeToUpdate, setMultiCurrencyCodeToUpdate] = useState<
    string | null
  >(null);
  const [showClearManyRowsModal, setShowClearManyRowsModal] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const isCurrencyPickerOpenRef = useRef(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const fieldRef = useRef<HTMLDivElement | null>(null);
  const fieldContentRef = useRef<HTMLSpanElement>(null);

  const dispatch = useDispatch();

  const addEditMark = () => {
    if (manuallyEdited) {
      setShowEditMark(true);
      setEditMode(false);
    }
  };

  useEffect(() => {
    if (editMode) {
      inputRef.current?.focus();
      setShowEditMark(false);
    } else {
      addEditMark();
    }
  }, [editMode]);

  const updateTaskMetaValue = (
    uuids: string | string[],
    uuid: string,
    newValue: any,
    customCurrencyCode?: string | null,
  ) => {
    if (tableContext === "workspaceTasksList") {
      dispatch(
        changeWorkspaceTaskMetaFieldValue(
          uuids,
          uuid,
          newValue,
          customCurrencyCode,
        ),
      );
    } else {
      dispatch(
        changeTaskMetaFieldValue(uuids, uuid, newValue, customCurrencyCode),
      );
    }
  };

  const onTableValueChange = (
    newValue: any,
    customCurrencyCode?: string | null,
  ) => {
    if (newValue || newValue === false || newValue === null) {
      const uuids = [objectId];
      if (
        selectedTasks.length > 1 &&
        selectedTasks.find((item: { id: string }) => item?.id === rowId)
      ) {
        setMultiValueToUpdate(newValue);
        setMultiCurrencyCodeToUpdate(customCurrencyCode ?? null);
        return;
      }
      updateTaskMetaValue(uuids, uuid, newValue, customCurrencyCode);
      dispatch(setLocalSyncToggle(true));
    }
  };

  const updateMultipleValues = () => {
    const uuids = selectedTasks.map((task: { taskId: string }) => {
      return task.taskId;
    });
    updateTaskMetaValue(
      uuids,
      uuid,
      multiValueToUpdate,
      multiCurrencyCodeToUpdate,
    );
    dispatch(setLocalSyncToggle(true));
    setMultiValueToUpdate(null);
    setMultiCurrencyCodeToUpdate(null);
  };

  const cancelMultiValueUpdate = () => {
    setMultiValueToUpdate(null);
    setMultiCurrencyCodeToUpdate(null);
    setKey(generateUuid());
  };

  const onValueChange = (newValue: any, customCurrencyCode?: string | null) => {
    if (newValue === undefined) return;
    return onTableValueChange
      ? onTableValueChange(newValue, customCurrencyCode)
      : null;
  };

  const updateMember = (newValue: { id: string }) => {
    if (newValue) {
      setMember(newValue);
      onValueChange(newValue.id);
    }
  };

  useEffect(() => {
    if (showEditMark !== manuallyEdited) {
      setShowEditMark(manuallyEdited);
    }
  }, [manuallyEdited]);

  useEffect(() => {
    if (disableFieldClick !== shouldTriggerEditWarning) {
      setDisableFieldClick(shouldTriggerEditWarning);
    }
  }, [shouldTriggerEditWarning]);

  useEffect(() => {
    if (type === MetaFieldType.Member) {
      const memberCopy = membersList?.find(
        (member: IMember) => member.id === fieldValue,
      );

      setMember(memberCopy);
    }
  }, [fieldValue, membersList]);

  const addEditMarkAfterUpdate = () => {
    if (
      (manuallyEdited && !shouldTriggerEditWarning) ||
      (!manuallyEdited && shouldTriggerEditWarning)
    ) {
      setShowEditMark(true);
      setEditMode(false);
    }
  };

  const emptyFieldClick = () => {
    setValue("");
    setEditMode(true);
    inputRef.current?.focus();
  };

  const clearTaskFieldValue = async () => {
    if (
      selectedTasks.length > 1 &&
      selectedTasks.find((item: { id: string }) => item.id === objectId)
    ) {
      const uuids = selectedTasks.map((task: { taskId: string }) => {
        return task.taskId;
      });

      try {
        updateTaskMetaValue(uuids, uuid, null);
      } catch {
        showToast(
          "error",
          <IDHFormattedMessage id="ws_error" defaultMessage="Error" />,
          <IDHFormattedMessage
            id="ws_something_went_wrong"
            defaultMessage="Something went wrong"
          />,
        );
      }
    } else {
      updateTaskMetaValue(objectId, uuid, null);
    }
  };

  const clearFieldValue = () => {
    if (objectId && uuid) {
      switch (dataType) {
        case tableDataType.Task:
          clearTaskFieldValue();
          break;
        case tableDataType.Project:
          dispatch(updateProjectMetaValue(objectId, uuid, null));
          break;
        default:
          break;
      }
    }
  };

  const isPartOfManyRowsSelection = () =>
    selectedTasks.length > 1 &&
    selectedTasks.find((item: { id: string }) => item?.id === objectId);

  const clearField = async () => {
    if (isPartOfManyRowsSelection()) {
      setShowClearManyRowsModal(true);
      return;
    }

    dispatch(setLocalSyncToggle(true));
    await setValue(undefined);
    setValue(null);
    setEditMode(false);
    clearFieldValue();
  };

  const clearMember = () => {
    if (isPartOfManyRowsSelection()) {
      setShowClearManyRowsModal(true);
      return;
    }

    setMember(null);
    clearFieldValue();
  };

  const confirmClearManyRows = async () => {
    dispatch(setLocalSyncToggle(true));
    await setValue(undefined);
    setValue(null);
    setMember(null);
    setEditMode(false);
    clearFieldValue();
    setShowClearManyRowsModal(false);
  };

  const cancelClearManyRows = () => {
    setShowClearManyRowsModal(false);
    setKey(generateUuid());
  };

  const textboxOnBlur = (newValue: string) => {
    dispatch(setTableInputFocused(false));
    if (!newValue?.length) {
      clearField();
    } else {
      onValueChange(newValue);
      addEditMarkAfterUpdate();
    }
  };

  const inputOnBlur = () => {
    if (isCurrencyPickerOpenRef.current) {
      return;
    }
    if (!value?.length) {
      clearField();
    } else {
      onValueChange(value, hasCustomCurrency ? cellCurrencyCode : null);
      addEditMarkAfterUpdate();
    }
    setEditMode(false);
  };

  const onDateChange = (newDate: string | boolean | number | null) => {
    onValueChange(newDate);
  };

  const onFieldClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (type === MetaFieldType.Text && e.target === e.currentTarget) {
      return;
    }

    setIsFocused(true);

    if (disableFieldClick && !showWarningModal) {
      setShowWarningModal(true);
    }
  };

  const confirmWarning = () => {
    setDisableFieldClick(false);
    setShowWarningModal(false);

    fieldContentRef?.current?.click();
  };

  const textBoxKeyBinding = (e: any) => {
    if (e.key === "Enter" && !(e.ctrlKey || e.altKey || e.shiftKey)) {
      return "not-handled";
    }
    return getDefaultKeyBinding(e);
  };

  const isContentMonitor = Boolean(data?.wsCreatorContentMonitorUuid);

  const isOutreachCount =
    type === MetaFieldType.Number && !!data?.isMessengerOutreach;

  const renderBasicTypes = () => {
    if (isContentMonitor) {
      const match = fieldValueSource?.match(REGEX.UUID);
      const monitorUuid = match?.[0] ?? data.wsCreatorContentMonitorUuid!;

      return (
        <ContentMonitorScore
          creatorId={rowId}
          monitorId={monitorUuid}
          value={fieldValue}
          projectId={projectId}
          status={data.ccmStatus}
          tooltip={data.tooltip}
        />
      );
    }

    if (data?.tooltip)
      return (
        <TooltipField tooltipContent={data?.tooltip} fieldValue={fieldValue} />
      );

    if (isOutreachCount) {
      return (
        <OutreachCount
          value={fieldValue}
          handleOpenMessageHistoryPreview={() => {
            dispatch(setCurrentTaskId(objectId));
            dispatch(setMessageHistoryPreviewOpen(true));
          }}
        />
      );
    }

    switch (type) {
      case MetaFieldType.Member:
        return member ? (
          <>
            <ChoosePersonDropdown
              uuid={uuid || ""}
              choosePerson={updateMember}
              dataType={
                tableContext === "workspaceTasksList"
                  ? tableDataType.GlobalTask
                  : tableDataType.Task
              }
              wsTeamUuids={data?.wsTeamUuids}
            >
              <div className="assignee-field">
                <img src={member.avatarUrl || AvatarPlaceholder} alt="avatar" />
                <span>
                  <TooltipedEllipsis
                    className="assignee-field__username"
                    maxWidth={160}
                    content={member.name}
                  />
                </span>
              </div>
            </ChoosePersonDropdown>
            {!disableFieldClick && (
              <div onClick={clearMember} className="tasks-table__field-close">
                <RemoveIcon />
              </div>
            )}
          </>
        ) : (
          <ChoosePersonDropdown
            uuid={uuid || ""}
            choosePerson={updateMember}
            dataType={
              tableContext === "workspaceTasksList"
                ? tableDataType.GlobalTask
                : tableDataType.Task
            }
            wsTeamUuids={data?.wsTeamUuids}
          >
            <div className="tasks-table__empty-field tasks-table__empty-field--circle">
              <PlusIcon />
            </div>
          </ChoosePersonDropdown>
        );

      case MetaFieldType.SingleSelect:
        return (
          <SelectDropdown
            uuid={uuid || ""}
            dataType={dataType}
            fieldData={fieldData}
            fieldValue={fieldValue}
            onValueChange={onValueChange}
            selectOptionType="single"
            wsSelectDataSetUuid={data?.wsSelectDataSetUuid}
            emptyFieldPlaceholder={
              <div className="ws-table__empty-field">
                <PlusIcon />
              </div>
            }
            removeValueButton={
              fieldValue &&
              !disableFieldClick && (
                <div onClick={clearField} className="tasks-table__field-close">
                  <RemoveIcon />
                </div>
              )
            }
            qaContext={dataType}
          />
        );

      case MetaFieldType.MultiSelect:
        return (
          <SelectDropdown
            uuid={uuid || ""}
            dataType={dataType}
            fieldData={fieldData}
            fieldValue={fieldValue}
            onValueChange={onValueChange}
            selectOptionType="multi"
            wsSelectDataSetUuid={data?.wsSelectDataSetUuid}
            stackLabels
            emptyFieldPlaceholder={
              <div className="ws-table__empty-field">
                <PlusIcon />
              </div>
            }
            removeValueButton={
              fieldValue &&
              !disableFieldClick && (
                <div onClick={clearField} className="tasks-table__field-close">
                  <RemoveIcon />
                </div>
              )
            }
          />
        );

      case MetaFieldType.AggregatedSelect:
        return (
          <>
            <AggregatedSelect
              uuid={uuid || ""}
              optionsData={fieldData?.data?.singleSelectOptions}
              fieldValue={fieldValue}
              updateValue={onValueChange}
              emptyFieldPlaceholder={
                <div className="ws-table__empty-field">
                  <PlusIcon />
                </div>
              }
            />
            {value && !disableFieldClick && (
              <div onClick={clearField} className="ws-table__field-close">
                <RemoveIcon />
              </div>
            )}
          </>
        );

      case MetaFieldType.Coupon:
        return (
          <>
            <Select
              mode="single"
              optionsData={couponsList
                .filter((coupon) =>
                  coupon.isAssigned
                    ? coupon?.wsTask?.taskId === objectId
                    : true,
                )
                ?.map((coupon) => ({
                  name: coupon.code,
                  value: coupon.uuid,
                  rank: "a",
                  color: "45485b",
                  backgroundColor: "f6f6f7",
                }))}
              uuid={uuid || ""}
              fieldValue={fieldValue}
              fieldName={name}
              variant="disable_all"
              updateValue={(newValue: any) => {
                onValueChange(newValue);
                dispatch(getCouponsList(activeWorkspaceUuid));
              }}
              emptyFieldPlaceholder={
                <div className="tasks-table__empty-field">
                  <PlusIcon />
                </div>
              }
              dataType={dataType}
            />
            {fieldValue && !disableFieldClick && (
              <div onClick={clearField} className="tasks-table__field-close">
                <RemoveIcon />
              </div>
            )}
          </>
        );

      case MetaFieldType.ProgressBar:
        return <ProgressField value={value || ""} options={data} />;

      case MetaFieldType.Date:
        const parsedDate = value ? new Date(value) : null;
        const safeValue =
          parsedDate && !isNaN(parsedDate.getTime()) ? value : null;

        if (value && !safeValue) {
          console.error("Date render error:", { value });
        }

        return (
          <>
            <DatePickerWrapper value={safeValue}>
              <CustomDatePicker
                initialDate={safeValue}
                dateChangeHandler={onDateChange}
                disableRedLabel={data?.disableRedLabel}
              />
            </DatePickerWrapper>

            {safeValue && (
              <div onClick={clearField} className="tasks-table__field-close">
                <RemoveIcon />
              </div>
            )}
          </>
        );

      case MetaFieldType.File:
        return (
          <TableFileField
            files={value}
            uuid={uuid}
            objectId={objectId}
            dataType={dataType}
            projectId={projectId}
            selectedTasks={selectedTasks}
            rowId={rowId}
            fieldName={fieldData.name}
          />
        );

      case MetaFieldType.Text:
        return value || value === "" ? (
          <>
            <TextBoxWrapper
              ref={inputRef}
              value={value || ""}
              onChange={setValue}
              onFocus={() => dispatch(setTableInputFocused(true))}
              onBlur={textboxOnBlur}
              customKeyBinding={textBoxKeyBinding}
              readOnly={isReadOnly || disableFieldClick}
            />

            {!isReadOnly && (
              <div
                onClick={clearField}
                className="tasks-table__field-close tasks-table__field-close--static"
              >
                <RemoveIcon />
              </div>
            )}
          </>
        ) : (
          <div className="tasks-table__empty-field" onClick={emptyFieldClick}>
            <PlusIcon />
          </div>
        );
      case MetaFieldType.AccessLink:
        return (
          <AccessLinkMetaField
            accessLinkValue={value}
            uuid={uuid}
            objectId={objectId}
            dataType={dataType}
          />
        );
      case MetaFieldType.BoolVal:
        return (
          <CustomCheckbox
            id={`${uuid}-${objectId}`}
            name={uuid}
            checked={value}
            onChange={(e) => {
              onValueChange(e.target.checked);
            }}
            blue
          />
        );
      case MetaFieldType.DictionaryElement:
        return (
          <>
            <DictionaryElementSelector
              value={fieldVal}
              wsDictionaryUuid={data?.wsDictionaryUuid}
              wsDictionarySubFieldUuid={data?.wsDictionarySubFieldUuid}
              wsDictionaryElementsFilter={data?.wsDictionaryElementsFilter}
              onValueChange={onValueChange}
              emptyFieldClick={emptyFieldClick}
              rowId={rowId}
            />
            {fieldValue && (
              <div onClick={clearField} className="tasks-table__field-close">
                <RemoveIcon />
              </div>
            )}
          </>
        );

      case MetaFieldType.Project:
        return (
          <ProjectSelector
            value={value}
            wsWorkspaceUuid={activeWorkspaceUuid}
          />
        );

      default:
        return value || value === "" ? (
          <>
            {editMode ? (
              <ProjectFieldEditSwitch
                fieldType={type}
                initialDate={value}
                dateChangeHandler={onDateChange}
                value={value}
                setValue={setValue}
                onBlur={inputOnBlur}
                inputRef={inputRef}
                prefix={
                  type === MetaFieldType.Currency
                    ? cellCurrencyCode
                    : fieldData?.data?.currencyCode || ""
                }
                columnCurrencyCode={data?.currencyCode || ""}
                onCurrencyChange={
                  type === MetaFieldType.Currency
                    ? handleCurrencyPickerSave
                    : undefined
                }
                onCurrencyPickerOpenChange={
                  type === MetaFieldType.Currency
                    ? (isOpen: boolean) => {
                        isCurrencyPickerOpenRef.current = isOpen;
                      }
                    : undefined
                }
              />
            ) : (
              <ProjectFieldPreviewSwitch
                data={fieldData}
                fieldType={type}
                setEditMode={setEditMode}
                value={value}
                prefix={
                  type === MetaFieldType.Currency
                    ? cellCurrencyCode
                    : fieldData?.data?.currencyCode || ""
                }
                dataType={tableDataType.Task}
              />
            )}

            <div
              onClick={clearField}
              className={classNames("tasks-table__field-close", {
                "tasks-table__field-close--hidden":
                  disableFieldClick || editMode,
              })}
            >
              <RemoveIcon />
            </div>
          </>
        ) : (
          <div className="tasks-table__empty-field" onClick={emptyFieldClick}>
            <PlusIcon />
          </div>
        );
    }
  };

  const renderComponentBasedOnDataType = () => {
    switch (dataType) {
      case tableDataType.Task:
        return (
          <TaskTableField
            fieldValue={fieldValue}
            fieldType={type}
            renderBasicTypes={renderBasicTypes}
            onValueChange={onValueChange}
            shouldTriggerEditWarning={disableFieldClick}
            clearFieldValue={clearFieldValue}
            tableContext={tableContext}
            projectId={projectId}
          />
        );

      case tableDataType.Project:
        return (
          <ProjectTableField
            fieldType={type}
            renderBasicTypes={renderBasicTypes}
          />
        );
      default:
        return null;
    }
  };

  useOnClickOutside(fieldRef, () => {
    setIsFocused(false);
  });

  const getCellUuidRecursively = (element: Element): string | null => {
    const { className } = element;
    if (className.includes("tasks-table__field-content")) {
      return element.getAttribute("data-cell-id");
    }
    if (element.parentElement) {
      return getCellUuidRecursively(element.parentElement);
    }
    return null;
  };

  const handleKeyPress = (e: KeyboardEvent) => {
    const ALLOWED_TYPES = [
      MetaFieldType.Text,
      MetaFieldType.Number,
      MetaFieldType.Currency,
      MetaFieldType.Percent,
    ];

    if (ALLOWED_TYPES.includes(type) && document.activeElement) {
      const cellUuid = getCellUuidRecursively(document.activeElement);

      if (cellUuid === `${rowId}-${uuid}`) {
        if (e.key === "Enter" && !(e.ctrlKey || e.altKey || e.shiftKey)) {
          e.preventDefault();
          (document.activeElement as HTMLInputElement)?.blur();
          setIsFocused(false);
          setEditMode(false);
        }

        if (e.key === "Escape") {
          e.preventDefault();
          if (type === MetaFieldType.Text) {
            setValue(`${fieldValue} `);
            setValue(fieldValue);
          } else {
            setValue(fieldValue);
          }
          (document.activeElement as HTMLInputElement)?.blur();
          setIsFocused(false);
          setEditMode(false);
        }
      }
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyPress);

    return () => {
      window.removeEventListener("keydown", handleKeyPress);
    };
  }, [fieldValue]);

  const nonReadOnly = isOutreachCount || isContentMonitor;
  const isGatheringData =
    typeof value === "string" && value?.toLowerCase()?.includes("gathering");

  return (
    <div
      key={key}
      ref={fieldRef}
      className={classNames(
        "tasks-table__field",
        `tasks-table__field--${type}`,
        {
          "tasks-table__field--hovered": isFocused,
          "tasks-table__field--read-only": isReadOnly && !nonReadOnly,
          "tasks-table__field--not-empty": value,
          "tasks-table__field--gathering-data": isGatheringData,
        },
      )}
      onClick={onFieldClick}
      data-task-title={title}
      data-task-column-key={columnKey}
      data-task-column-name={columnName}
    >
      <span
        key={type !== MetaFieldType.MultiSelect ? fieldValue?.toString() : uuid}
        ref={fieldContentRef}
        className={classNames("tasks-table__field-content", {
          "tasks-table__field-content--disabled": disableFieldClick,
        })}
        data-cell-id={`${rowId}-${uuid}`}
      >
        {renderComponentBasedOnDataType()}
        {showEditMark && !overrideValue && fieldValue && (
          <div className="tasks-table__field-content-edit-icon">
            <EditIcon />
            <IDHFormattedMessage id="ws_edited" defaultMessage="Edited" />
          </div>
        )}
      </span>

      {showWarningModal && (
        <FieldSynchronizationWarningModal
          onClose={() => setShowWarningModal(false)}
          onAccept={confirmWarning}
        />
      )}

      {multiValueToUpdate && (
        <ChangeWillAffectManyRowsModal
          onClose={() => setMultiValueToUpdate(null)}
          fieldName={fieldData.name}
          rowsNumber={selectedTasks.length}
          onConfirmClick={updateMultipleValues}
          onCancelClick={cancelMultiValueUpdate}
        />
      )}

      {showClearManyRowsModal && (
        <ChangeWillAffectManyRowsModal
          onClose={() => setShowClearManyRowsModal(false)}
          fieldName={fieldData.name}
          rowsNumber={selectedTasks.length}
          onConfirmClick={confirmClearManyRows}
          onCancelClick={cancelClearManyRows}
        />
      )}
    </div>
  );
}
